import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { ThemeContext } from '../../context/ThemeContext';
import { API_BASE } from '../../api';

function TaskFullDetails() {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const { currentTheme } = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();


  // 🎨 GLOBAL COLORS DEFINITION
  const colors = {
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  };

  // Video type configuration
  const videoTypeConfig = {
    'indoor_reel': { label: '🏠 Indoor Reel', color: '#6366F1' },
    'outdoor_reel': { label: '🌳 Outdoor Reel', color: '#10B981' },
    'podcast': { label: '🎙️ Podcast', color: '#F59E0B' },
    'indoor_long_video': { label: '🏠 Indoor Long', color: '#8B5CF6' },
    'outdoor_long_video': { label: '🌳 Outdoor Long', color: '#06B6D4' }
  };

  // Compact styles
  const styles = {
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
      position: 'relative',
      overflowX: 'hidden'
    },
    animatedBackground: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none'
    },
    floatingOrb: {
      position: 'absolute',
      borderRadius: '50%',
      filter: 'blur(40px)',
      opacity: 0.3,
      animation: 'float 8s ease-in-out infinite'
    },
    orb1: {
      width: '300px',
      height: '300px',
      background: colors.primary,
      top: '10%',
      left: '5%',
      animationDelay: '0s'
    },
    orb2: {
      width: '200px',
      height: '200px',
      background: colors.secondary,
      top: '60%',
      right: '10%',
      animationDelay: '2s'
    },
    orb3: {
      width: '250px',
      height: '250px',
      background: colors.accent,
      bottom: '20%',
      left: '15%',
      animationDelay: '4s'
    },
    orb4: {
      width: '180px',
      height: '180px',
      background: colors.success,
      top: '30%',
      right: '20%',
      animationDelay: '6s'
    },
    gridOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
      backgroundSize: '50px 50px'
    },
    header: {
      padding: '30px 30px 20px',
      position: 'relative',
      zIndex: 2
    },
    headerContent: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    greeting: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      marginBottom: '12px'
    },
    pageTitle: {
      fontSize: '2.2rem',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      margin: 0
    },
    welcomeBadge: {
      background: 'rgba(255, 255, 255, 0.1)',
      padding: '6px 12px',
      borderRadius: '16px',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    badgeText: {
      color: 'white',
      fontWeight: '600',
      fontSize: '0.8rem'
    },
    badgeRole: {
      background: colors.primary,
      color: 'white',
      padding: '2px 6px',
      borderRadius: '10px',
      fontSize: '0.65rem',
      fontWeight: '600',
      textTransform: 'uppercase'
    },
    pageSubtitle: {
      color: colors.textLight,
      fontSize: '1rem',
      margin: 0,
      maxWidth: '600px'
    },
    messageAlert: {
      color: 'white',
      padding: '15px',
      borderRadius: '12px',
      margin: '0 30px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      maxWidth: '1200px',
      marginLeft: 'auto',
      marginRight: 'auto',
      fontSize: '0.9rem'
    },
    alertIcon: {
      fontSize: '1.2rem'
    },
    alertMessage: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '3px',
      color: 'white'
    },
    alertClose: {
      background: 'none',
      border: 'none',
      color: colors.danger,
      cursor: 'pointer',
      padding: '6px',
      borderRadius: '6px',
      transition: 'background 0.3s ease',
      fontSize: '0.8rem'
    },
    content: {
      padding: '0 30px 30px',
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 2
    },
    formCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '20px',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
    },
    cardHeader: {
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
      color: 'white',
      padding: '20px 25px'
    },
    cardTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      margin: 0
    },
    cardBody: {
      padding: '25px'
    },
    backBtn: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      border: `1px solid ${colors.borderDark}`,
      padding: '10px 18px',
      borderRadius: '10px',
      fontWeight: '600',
      fontSize: '0.9rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      textDecoration: 'none',
      marginBottom: '15px'
    },
    loadingState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '80px 30px',
      color: 'white',
      textAlign: 'center'
    },
    loadingText: {
      fontSize: '1.6rem',
      margin: '0 0 15px 0',
      fontWeight: '600',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    loadingSubtext: {
      color: colors.textLight,
      margin: 0,
      fontSize: '1rem'
    },
    spinner: {
      width: '18px',
      height: '18px',
      border: '2px solid transparent',
      borderTop: '2px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    tableHeader: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      borderBottom: `2px solid ${colors.borderDark}`,
      padding: '10px 8px',
      fontWeight: '600',
      fontSize: '0.8rem',
      whiteSpace: 'nowrap'
    },
    tableRow: {
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      color: 'white',
      fontSize: '0.8rem'
    },
    tableCell: {
      padding: '8px',
      verticalAlign: 'middle',
      fontSize: '0.8rem'
    },
    smallBadge: {
      padding: '3px 8px',
      borderRadius: '8px',
      fontSize: '0.7rem',
      fontWeight: '600',
      border: 'none'
    },
    linkButton: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      border: `1px solid ${colors.borderDark}`,
      padding: '4px 8px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '0.7rem',
      fontWeight: '600',
      opacity: 0.8,
      transition: 'all 0.3s ease'
    },
    videoTypeBadge: {
      background: 'rgba(139, 92, 246, 0.2)',
      color: '#8B5CF6',
      border: '1px solid rgba(139, 92, 246, 0.3)',
      padding: '3px 6px',
      borderRadius: '6px',
      fontSize: '0.7rem',
      fontWeight: '500'
    },
    sectionHeader: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: colors.primary,
      fontWeight: 'bold',
      fontSize: '0.9rem'
    }
  };

  useEffect(() => {
    if (location.state?.task) {
      setTask(location.state.task);
      setLoading(false);
    } else {
      const taskId = new URLSearchParams(location.search).get('id');
      if (taskId) {
        fetchTaskDetails(taskId);
      } else {
        setMessage('❌ No task data provided');
        setLoading(false);
      }
    }
  }, [location]);

  const fetchTaskDetails = async (taskId) => {
    try {
      setLoading(true);
      setMessage('');

      const response = await fetch(`${API_BASE}/get_task_full_details.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ task_id: taskId })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to fetch task details');
      }

      setTask(data.task || data.data?.task);
    } catch (error) {
      console.error('❌ Error fetching task details:', error);
      setMessage('❌ Error loading task details: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkClick = (link) => {
    if (link && link !== '-' && link !== 'NULL' && link !== 'null') {
      let formattedLink = link;
      if (!formattedLink.startsWith('http://') && !formattedLink.startsWith('https://')) {
        formattedLink = 'https://' + formattedLink;
      }
      window.open(formattedLink, '_blank', 'noopener,noreferrer');
    }
  };

  const isLinkAvailable = (link) => {
    return link && link !== '-' && link !== 'NULL' && link !== 'null' && link.trim() !== '';
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'NULL' || dateString === '0000-00-00') return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'bg-warning text-dark', text: 'Pending', icon: 'fa-clock' },
      assigned: { class: 'bg-primary text-white', text: 'Assigned', icon: 'fa-user-check' },
      in_progress: { class: 'bg-info text-white', text: 'In Progress', icon: 'fa-spinner' },
      completed: { class: 'bg-success text-white', text: 'Completed', icon: 'fa-check' },
      approved: { class: 'bg-success text-white', text: 'Approved', icon: 'fa-check-circle' },
      needs_revision: { class: 'bg-warning text-dark', text: 'Needs Revision', icon: 'fa-sync' },
      rejected: { class: 'bg-danger text-white', text: 'Rejected', icon: 'fa-times' },
      cancelled: { class: 'bg-danger text-white', text: 'Cancelled', icon: 'fa-times' },
      unassigned: { class: 'bg-secondary text-white', text: 'Unassigned', icon: 'fa-user-times' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`badge ${config.class}`} style={styles.smallBadge}>
        <i className={`fas ${config.icon} me-1`}></i>
        {config.text}
      </span>
    );
  };

  const getVideoTypeBadge = (videoType) => {
    if (!videoType) return (
      <span 
        className="badge" 
        style={{
          ...styles.videoTypeBadge,
          background: 'rgba(255, 255, 255, 0.1)',
          color: colors.textLight,
          borderColor: colors.borderDark
        }}
        title="No video type specified"
      >
        -
      </span>
    );
    
    const config = videoTypeConfig[videoType] || { 
      label: videoType, 
      color: '#6B7280' 
    };
    
    return (
      <span 
        className="badge" 
        style={{
          ...styles.videoTypeBadge,
          background: `${config.color}20`,
          color: config.color,
          borderColor: `${config.color}30`,
          cursor: 'help'
        }}
        title={config.label}
      >
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div className="animated-background" style={styles.animatedBackground}>
            <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
            <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
            <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
            <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
            <div className="grid-overlay" style={styles.gridOverlay}></div>
          </div>
          <div style={styles.loadingState}>
            <div className="loading-spinner" style={styles.spinner}></div>
            <h3 style={styles.loadingText}>Loading Task Details</h3>
            <p style={styles.loadingSubtext}>Please wait while we fetch task information</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!task) {
    return (
      <Layout>
        <div style={styles.container}>
          <div className="animated-background" style={styles.animatedBackground}>
            <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
            <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
            <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
            <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
            <div className="grid-overlay" style={styles.gridOverlay}></div>
          </div>
          <div style={styles.content}>
            <div style={styles.messageAlert}>
              <div className="alert-icon" style={{...styles.alertIcon, color: colors.danger}}>
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div className="alert-message" style={styles.alertMessage}>
                <strong>Error</strong>
                <span style={{color: 'white'}}>{message || 'Task not found'}</span>
              </div>
            </div>
            <button 
              style={styles.backBtn}
              onClick={() => navigate(-1)}
            >
              <i className="fas fa-arrow-left me-1"></i>
              Go Back
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        {/* Animated Background */}
        <div className="animated-background" style={styles.animatedBackground}>
          <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
          <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
          <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
          <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
          <div className="grid-overlay" style={styles.gridOverlay}></div>
        </div>

        {/* Header Section */}
        <div className="task-details-header" style={styles.header}>
          <div className="header-content" style={styles.headerContent}>
            <div className="welcome-section">
              <div className="greeting" style={styles.greeting}>
                <h1 className="page-title" style={styles.pageTitle}>
                  <i className="fas fa-info-circle me-2"></i>
                  Task Details
                </h1>
                <div className="welcome-badge" style={styles.welcomeBadge}>
                  <span className="badge-text" style={styles.badgeText}>Task Management</span>
                  <span className="badge-role" style={styles.badgeRole}>Details</span>
                </div>
              </div>
              <p className="page-subtitle" style={styles.pageSubtitle}>
                Complete information about the video task
              </p>
            </div>
            <div style={{marginTop: '15px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
              <span className="badge" style={{background: 'rgba(255, 255, 255, 0.2)', color: 'white', padding: '6px 12px', borderRadius: '10px', fontSize: '0.8rem'}}>
                ID: #{task.id}
              </span>
              {getStatusBadge(task.status)}
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div className="message-alert" style={{
            ...styles.messageAlert,
            background: message.includes('✅') 
              ? 'rgba(16, 185, 129, 0.1)' 
              : 'rgba(220, 53, 69, 0.1)',
            border: message.includes('✅') 
              ? `1px solid ${colors.success}`
              : `1px solid ${colors.danger}`,
            color: message.includes('✅') 
              ? colors.success
              : colors.danger
          }}>
            <div className="alert-icon" style={{...styles.alertIcon, 
              color: message.includes('✅') 
                ? colors.success
                : colors.danger
            }}>
              <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
            </div>
            <div className="alert-message" style={styles.alertMessage}>
              <strong>{message.includes('✅') ? 'Success' : 'Error'}</strong>
              <span style={{color: 'white'}}>{message}</span>
            </div>
            <button className="alert-close" onClick={() => setMessage('')} style={styles.alertClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="task-details-content" style={styles.content}>
          <button 
            style={styles.backBtn}
            onClick={() => navigate(-1)}
          >
            <i className="fas fa-arrow-left me-1"></i>
            Back
          </button>

          {/* Task Details Card */}
          <div className="details-card" style={styles.formCard}>
            <div className="card-header" style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>
                <i className="fas fa-table me-2"></i>
                Task Information
              </h3>
            </div>
            <div style={{padding: '0'}}>
              <div style={{overflowX: 'auto'}}>
                <table className="table table-hover mb-0" style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem'}}>
                  <thead>
                    <tr>
                      <th style={styles.tableHeader}>Field</th>
                      <th style={styles.tableHeader}>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Basic Information */}
                    <tr style={{...styles.tableRow, ...styles.sectionHeader}}>
                      <td colSpan="2">
                        <i className="fas fa-info-circle me-2"></i>
                        Basic Info
                      </td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Task ID</td>
                      <td style={{...styles.tableCell, fontWeight: 'bold', color: colors.primary}}>#{task.id}</td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Video Name</td>
                      <td style={{...styles.tableCell, fontWeight: 'bold'}}>{task.videoName || task.video_name || 'N/A'}</td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Video Type</td>
                      <td style={styles.tableCell}>
                        {getVideoTypeBadge(task.video_type)}
                      </td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Video Count</td>
                      <td style={styles.tableCell}>
                        <span className="badge" style={{background: colors.accent, color: 'white', ...styles.smallBadge}}>
                          <i className="fas fa-video me-1"></i>
                          {task.videoCount || task.video_count || 1}
                        </span>
                      </td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Status</td>
                      <td style={styles.tableCell}>{getStatusBadge(task.status)}</td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Shoot Date</td>
                      <td style={styles.tableCell}>{formatDate(task.shootDate || task.shoot_date)}</td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Deadline</td>
                      <td style={styles.tableCell}>{formatDate(task.deadlineDate || task.deadline_date)}</td>
                    </tr>

                    {/* Team Information */}
                    <tr style={{...styles.tableRow, ...styles.sectionHeader}}>
                      <td colSpan="2">
                        <i className="fas fa-users me-2"></i>
                        Team Info
                      </td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Client</td>
                      <td style={{...styles.tableCell, fontWeight: 'bold'}}>{task.client || task.client_name || 'N/A'}</td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Operator</td>
                      <td style={styles.tableCell}>
                        <i className="fas fa-camera me-1" style={{color: colors.primary}}></i>
                        {task.operatorName || task.operator_name || 'N/A'}
                      </td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Editor</td>
                      <td style={styles.tableCell}>
                        <i className="fas fa-user-edit me-1" style={{color: colors.success}}></i>
                        {task.editor || task.editor_name || 'Unassigned'}
                      </td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Designer</td>
                      <td style={styles.tableCell}>
                        <i className="fas fa-palette me-1" style={{color: colors.accent}}></i>
                        {task.designerName || task.designer_name || 'Unassigned'}
                      </td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Assigned By</td>
                      <td style={styles.tableCell}>{task.assignedByName || task.assigned_by_name || 'SuperAdmin'}</td>
                    </tr>

                    {/* Video Links */}
                    <tr style={{...styles.tableRow, ...styles.sectionHeader}}>
                      <td colSpan="2">
                        <i className="fas fa-link me-2"></i>
                        Video Links
                      </td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Raw Video</td>
                      <td style={styles.tableCell}>
                        <button 
                          className="btn btn-sm"
                          onClick={() => handleLinkClick(task.rawLink || task.raw_link)}
                          disabled={!isLinkAvailable(task.rawLink || task.raw_link)}
                          style={{
                            ...styles.linkButton,
                            background: isLinkAvailable(task.rawLink || task.raw_link) 
                              ? `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`
                              : 'rgba(255, 255, 255, 0.1)',
                            opacity: isLinkAvailable(task.rawLink || task.raw_link) ? 1 : 0.4,
                          }}
                        >
                          <i className="fas fa-external-link-alt me-1"></i>
                          {isLinkAvailable(task.rawLink || task.raw_link) ? 'Open' : 'No Link'}
                        </button>
                      </td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Reference Video</td>
                      <td style={styles.tableCell}>
                        <button 
                          className="btn btn-sm"
                          onClick={() => handleLinkClick(task.referenceVideoLink || task.reference_video_link)}
                          disabled={!isLinkAvailable(task.referenceVideoLink || task.reference_video_link)}
                          style={{
                            ...styles.linkButton,
                            background: isLinkAvailable(task.referenceVideoLink || task.reference_video_link) 
                              ? `linear-gradient(135deg, ${colors.accent}, ${colors.primary})`
                              : 'rgba(255, 255, 255, 0.1)',
                            opacity: isLinkAvailable(task.referenceVideoLink || task.reference_video_link) ? 1 : 0.4,
                          }}
                        >
                          <i className="fas fa-external-link-alt me-1"></i>
                          {isLinkAvailable(task.referenceVideoLink || task.reference_video_link) ? 'Open' : 'No Link'}
                        </button>
                      </td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Edited Video</td>
                      <td style={styles.tableCell}>
                        <button 
                          className="btn btn-sm"
                          onClick={() => handleLinkClick(task.editorLink || task.edited_video_link || task.edited_link)}
                          disabled={!isLinkAvailable(task.editorLink || task.edited_video_link || task.edited_link)}
                          style={{
                            ...styles.linkButton,
                            background: isLinkAvailable(task.editorLink || task.edited_video_link || task.edited_link) 
                              ? `linear-gradient(135deg, ${colors.success}, ${colors.accent})`
                              : 'rgba(255, 255, 255, 0.1)',
                            opacity: isLinkAvailable(task.editorLink || task.edited_video_link || task.edited_link) ? 1 : 0.4,
                          }}
                        >
                          <i className="fas fa-external-link-alt me-1"></i>
                          {isLinkAvailable(task.editorLink || task.edited_video_link || task.edited_link) ? 'Open' : 'No Link'}
                        </button>
                      </td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Thumbnail</td>
                      <td style={styles.tableCell}>
                        <button 
                          className="btn btn-sm"
                          onClick={() => handleLinkClick(task.thumbnailLink || task.thumbnail_link)}
                          disabled={!isLinkAvailable(task.thumbnailLink || task.thumbnail_link)}
                          style={{
                            ...styles.linkButton,
                            background: isLinkAvailable(task.thumbnailLink || task.thumbnail_link) 
                              ? `linear-gradient(135deg, ${colors.warning}, ${colors.accent})`
                              : 'rgba(255, 255, 255, 0.1)',
                            opacity: isLinkAvailable(task.thumbnailLink || task.thumbnail_link) ? 1 : 0.4,
                          }}
                        >
                          <i className="fas fa-image me-1"></i>
                          {isLinkAvailable(task.thumbnailLink || task.thumbnail_link) ? 'View' : 'No Link'}
                        </button>
                      </td>
                    </tr>

                    {/* Notes Section */}
                    <tr style={{...styles.tableRow, ...styles.sectionHeader}}>
                      <td colSpan="2">
                        <i className="fas fa-sticky-note me-2"></i>
                        Notes
                      </td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Task Notes</td>
                      <td style={styles.tableCell}>
                        <div style={{ 
                          border: `1px solid ${colors.borderDark}`, 
                          borderRadius: '8px', 
                          padding: '12px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          fontSize: '0.8rem'
                        }}>
                          {task.notes ? (
                            <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: 'white' }}>{task.notes}</p>
                          ) : (
                            <p style={{ margin: 0, color: colors.textLight }}>No task notes</p>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Editor Notes</td>
                      <td style={styles.tableCell}>
                        <div style={{ 
                          border: `1px solid ${colors.borderDark}`, 
                          borderRadius: '8px', 
                          padding: '12px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          fontSize: '0.8rem'
                        }}>
                          {task.editorNotes || task.editor_notes ? (
                            <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: 'white' }}>{task.editorNotes || task.editor_notes}</p>
                          ) : (
                            <p style={{ margin: 0, color: colors.textLight }}>No editor notes</p>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={{...styles.tableCell, fontWeight: '600', color: colors.textLight}}>Designer Notes</td>
                      <td style={styles.tableCell}>
                        <div style={{ 
                          border: `1px solid ${colors.borderDark}`, 
                          borderRadius: '8px', 
                          padding: '12px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          fontSize: '0.8rem'
                        }}>
                          {task.designerNotes || task.designer_notes ? (
                            <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: 'white' }}>{task.designerNotes || task.designer_notes}</p>
                          ) : (
                            <p style={{ margin: 0, color: colors.textLight }}>No designer notes</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes float {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }

        .table {
          --bs-table-bg: transparent;
          --bs-table-color: white;
        }

        .table-hover tbody tr:hover {
          background: rgba(255, 255, 255, 0.05) !important;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .header, .content {
            padding: 20px 15px !important;
          }
          
          .page-title {
            font-size: 1.8rem !important;
          }
          
          .card-body {
            padding: 15px !important;
          }
        }
      `}</style>
    </Layout>
  );
}

export default TaskFullDetails;